import { ApiFilter, ApiPage, ApiSort } from "@result/rui/utils/api";
import * as uuid from "uuid";

import GenericPokkService from "#/data-layer/GenericPokkService";
import * as DLD from "#/def/data-layer";

import { funcFilter } from "./DataLayerFilter";
import { funcSort } from "./DataLayerSort";
import idb from "./idb";

export const DIRTY_STATE_CREATE = "CREATE";
export const DIRTY_STATE_UPDATE = "UPDATE";
export const DIRTY_STATE_DELETE = "DELETE";
export const DIRTY_STATE_UPLOAD = "UPLOAD";

/**
 * Generic Offline Store
 */
export default class GenericOfflineStore {
  /**
   * @var {string} ident - Store identifier, name
   */
  ident;

  /**
   * @var {IndexedDB} idb - IndexedDB instance to interact with the local store
   */
  idb;

  /**
   * @var {PokkService} service - Service to interact with the API
   */
  dataLayer;

  /**
   * @var {function} systemMapper - Function to map items..
   */
  systemMapper;

  /**
   * @var {function} systemFilter - Function to apply additional client-side filtering to the results from the service, if needed. It should take an item and return true if it should be included in the results, or false if it should be excluded.
   */
  systemFilter;

  /**
   * Are we online?
   *
   * This can be determined by a custom function provided in the constructor params,
   * or by using the useOnlineStatus hook if available, or by default it will
   * always return true (considering the app always online).
   *
   * @var {function} isOnline
   * @returns boolean
   */
  isOnline;

  /**
   * Constructor for the OfflineStore class.
   *
   * @param {object} params
   * @param {PokkService} params.service - Service to interact with the API
   * @param {IndexedDB} params.idb - IndexedDB instance to interact with the local store
   * @param {string} params.ident - Store identifier, name
   * @param {object} params.dataLayer - Optional full data layer instance or configuration object to use instead of the default GenericPokkService instance. If an object is provided, it will be used to construct a new GenericPokkService instance.
   * @param {function} params.systemMapper - Optional function to map items before storing them in IndexedDB. It should take an item and return the mapped item. This can be useful if you want to store only a subset of the data, or if you want to transform the data in some way before storing it.
   * @param {function} params.systemFilter - Optional function to apply additional client-side filtering to the results from the service. It should take an item and return true if it should be included in the results, or false if it should be excluded.
   */
  constructor($params) {
    this.ident = $params.ident;

    if (this.ident.startsWith("sif/")) {
      console.warn(
        `You are configuring GenericOfflineStore to use db name that begins with sif/.` +
          `You might want to use ${DLD.LOOKUP_TABLES} as the identifier instead, since
        lookup tables are prefetched from multiple endpoints and
        stored in the same IndexedDB database.`
      );
    }

    this.isOnline = $params.isOnline ?? (() => navigator.onLine);

    this.idb = $params.idb ?? idb;

    // initializes the data layer based on the provided configuration
    // if a full dataLayer instance is provided, use it directly,
    // if a configuration object is provided, use it to construct a new GenericPokkService instance,
    // if neither is provided, construct a new GenericPokkService instance with the identifier and cache configuration
    this.dataLayer = (() => {
      if (typeof $params.dataLayer !== "object") {
        // construct a new GenericPokkService instance with the
        // provided identifier and optional cache configuration
        return new GenericPokkService({
          baseUrl: $params.ident,
        });
      }

      if ("query" in $params.dataLayer) {
        // assuming we have a full data layer instance, we can just use it as is
        return $params.dataLayer;
      }

      // if we have a dataLayer configuration object,
      // we can use it to construct a new GenericPokkService instance
      return new GenericPokkService($params.dataLayer);
    })();

    this.systemMapper = $params.systemMapper ?? ((item) => item);
    this.systemFilter = $params.systemFilter ?? (() => true);

    // @todo write sync logic and register with sync service if provided
    // if (syncService) {
    //   // registers itself with the sync service,
    //   // so that it can be notified when to sync
    //   syncService.register(this);
    // }
  }

  /**
   * Queries items with given filter/sort/page, and returns them as a list.
   *
   * It will use the service's query method,
   * but can also apply additional client-side filtering/sorting/pagination if needed.
   *
   * @param {object} param
   * @param {function|ApiFilter} param.filter
   * @param {ApiFilter} param.apiFilter - Optional expilicit API filter (if different from filter)
   * @param {function|ApiSort} param.sort
   * @param {ApiSort} param.apiSort - Optional expilicit API sort (if different from sort)
   * @param {number|ApiPage} param.page
   * @param {number} param.perPage - Optional number of items per page
   * @returns
   */
  async all() {
    if (!this.isOnline()) {
      return this.idb.all(this.ident);
    }
    throw new Error(
      "GenericOfflineStore.all() is only available for indexedDB"
    );
  }

  async query($params) {
    if (!this.isOnline()) {
      // we're offline
      // return what we have stored for offline use
      return this.rawQuery($params);
    }

    // fetch live data from the service,
    const result = await this.dataLayer.query($params);

    // store whatever we fetched from the API into offline storage,
    // so that it's available for offline use later
    await this.rawInsert(...result.content);

    return result;
  }

  async rawQuery($params) {
    // filtering function that combines the store's filter
    // and the provided filter param (if any)
    const filter = (item) =>
      this.systemFilter(item) && (funcFilter($params.filter)?.(item) ?? true);

    // sorting function extracted from the provided sort param (if any)
    const sort = funcSort($params.sort);

    // idb.query understands flat pageNo/pageSize only — translate the `page`
    // param, which arrives as {page, size} from useTable-driven components
    // or as ApiPage {pageNumber, pageSize}, so offline pagination works
    const pageNo =
      $params.page?.pageNumber ?? $params.page?.page ?? $params.pageNo ?? 0;
    const pageSize =
      $params.page?.pageSize ?? $params.page?.size ?? $params.pageSize ?? 20;

    return this.idb.query(this.ident, {
      ...$params,
      filter,
      sort,
      pageNo,
      pageSize,
    });
  }

  /**
   *
   * @param {string} id
   */
  async read(id) {
    if (!this.isOnline()) {
      // offline, return stored data
      const item = await this.idb.read(this.ident, id);
      // when offline and not in IDB, return null — do not attempt a network call
      return item ?? null;
    }

    // fetch live data
    let item = await this.dataLayer.read(id);

    if (item.id == null) {
      item.id = id;
    }
    await this.rawInsert(item);

    return item;
  }

  /**
   * Creates a new item with the given payload.
   *
   * When not online it creates the item in the local store and mark it as "dirty" (needing sync).
   *
   * @param {*} payload
   * @param {object} $params
   * @param {boolean} param.isOffline - If true, forces using offline data (if available) instead of querying the service. Default is false.
   */
  async create(payload) {
    if (!this.isOnline()) {
      // offline, update local store only
      return this.rawInsert({
        // generate a ID for the new item,
        // so that it can be referenced later for updates/deletes and syncing
        //
        // user can also provide their own ID in the payload if they want to,
        // but if not we'll generate one here

        ...payload,
        id: payload?.id ?? uuid.v4(),

        // mark the item as needing sync,
        $dirty: DIRTY_STATE_CREATE,
      });
    } else {
      return this.dataLayer.create(payload);
    }
  }

  /**
   * Updates item with given id using the payload.
   *
   * When offline it updates the local data and mark it as "dirty" (needing sync).
   *
   * @param {string} id
   * @param {object} payload
   * @param {object} $params
   */
  async update(id, payload) {
    if (!this.isOnline()) {
      // offline, update local store only
      return this.rawInsert({
        ...payload,

        // we want to occupy a new record in the store for the updated item,
        // so that we can keep the original data intact for reference during syncing,
        // and also to allow multiple updates while offline without losing track of the changes
        id: `${id}//${DIRTY_STATE_UPDATE}`,

        // mark the item as needing sync,
        $dirty: DIRTY_STATE_UPDATE,
        $dirtyId: id,
      });
    } else {
      return this.dataLayer.update(id, payload);
    }
  }

  /**
   * Marks item with given id as deleted or sends delete request to the service.
   *
   * When offline it marks the item as "dirty" (needing sync) and deleted,
   * so that it can be removed from the service when back online.
   *
   * @param {string} id
   * @param {object} $params
   */
  async delete(id, version) {
    if (!this.isOnline()) {
      // offline, update local store only

      return this.rawInsert({
        // we want to occupy a new record in the store for the updated item,
        // so that we can keep the original data intact for reference during syncing,
        // and also to allow multiple updates while offline without losing track of the changes
        id: `${id}//${DIRTY_STATE_DELETE}`,

        // mark the item as needing sync,
        // with the type of operation (create/update/delete)
        $dirty: DIRTY_STATE_DELETE,
        $dirtyId: id,
      });
    } else {
      return this.dataLayer.delete(id, version);
    }
  }

  /**
   * Stores a file blob to the indexedDB, and mark it as "dirty" (needing sync).
   *
   * @param {Blob} blob
   * @param {object} meta
   */
  async upload(blob, dto = {}) {
    if (!this.isOnline()) {
      // offline, insert upload to local store only
      const localItem = {
        // generate a ID for the new item,
        // so that it can be referenced later for updates/deletes and syncing
        //
        // user can also provide their own ID in the payload if they want to,
        // but if not we'll generate one here
        id: uuid.v4(),

        $blob: blob,
        $dto: dto,

        // mark the item as needing sync,
        $dirty: DIRTY_STATE_UPLOAD,
      };

      await this.rawInsert(localItem);
      return localItem;
    } else {
      return this.dataLayer.upload(blob, dto);
    }
  }

  /**
   * Syncs any local changes with the service.
   *
   * This should be called when the app goes back online, to sync any changes made while offline.
   */
  async sync(...items) {
    if (!this.isOnline()) {
      throw new Error("Cannot sync while offline");
    }

    if (items.length > 0) {
      // sync only the provided items
      return Promise.all(items.map((item) => this.syncItem(item)));
    }

    return this.idb
      .query(this.ident, {
        // get only items that are marked as dirty and match the filter
        filter: (item) => this.systemFilter(item) && item.$dirty,
      })
      .then(async ({ content }) => {
        for await (const item of content) {
          await this.syncItem(item);
        }
      });
  }

  async syncItem(item) {
    if (DIRTY_STATE_CREATE === item.$dirty) {
      // send create request to the service with the item data
      // response from the service should include the created
      // item with its new ID (when the service generates IDs),
      // which we can then use to update the local store
      //
      // this will effectively replace the temporary ID
      // we generated for the new item while offline
      // with the actual ID from the service,
      return this.dataLayer
        .create({
          ...item,

          // prune dirty state props from the payload
          $dirty: undefined,
          $dirtyId: undefined,
        })
        .then(async (fresh) => {
          await this.rawInsert(fresh);
          // remove the offline dirty record — the fresh server record replaces it
          await this.rawDelete(item.id);
          return fresh;
        });
    }

    if (DIRTY_STATE_UPDATE === item.$dirty) {
      // send update request to the service with the item data and the original ID (from $dirtyId)
      return this.dataLayer
        .update(item.$dirtyId, {
          ...item,

          // prune dirty state props from the payload
          id: undefined,
          $dirty: undefined,
          $dirtyId: undefined,
        })
        .then(async (fresh) => {
          await this.rawInsert(fresh);
          // remove the dirty shadow record (id = "${$dirtyId}//UPDATE")
          await this.rawDelete(item.id);
          return fresh;
        });
    }

    if (DIRTY_STATE_DELETE === item.$dirty) {
      // send delete request to the service with the original ID (from $dirtyId)
      return this.dataLayer
        .delete(item.$dirtyId)
        .then(() => this.rawDelete(item.id));
    }

    if (DIRTY_STATE_UPLOAD === item.$dirty) {
      // send upload request to the service with the file blob and dto
      // TODO: Reconcile the temporary local upload id with the backend
      // attachment id returned by the upload endpoint instead of discarding
      // the server response after sync.
      return this.dataLayer
        .upload(item.$blob, item.$dto)
        .then(() => this.rawDelete(item.id));
    }

    // eslint-disable-next-line no-console
    console.warn(
      `Unknown dirty state ${item.$dirty} for item with id ${item.id}, skipping sync`
    );
  }

  /**
   * Manually inserts a new item to the store, without syncing with the service.
   *
   * @param {object} item
   */
  async rawInsert(...items) {
    return this.idb.insert(this.ident, ...items.map(this.systemMapper));
  }

  /**
   * Manually removes an item from the store, without syncing with the service.
   *
   * @param {string} id
   */
  async rawDelete(...ids) {
    return this.idb.delete(this.ident, ...ids);
  }

  async rawClear() {
    return this.idb.clearStore(this.ident);
  }
}
